/**
 * Shared theme logic - one source of truth for the admin preview (`web_admin`)
 * and the public renderer (`web_site`). The theme is stored in `Site.themeJson`.
 *
 * Contains: types (colors, gradient, per-element fonts), the Google Fonts URL
 * builder and the element-targeted font CSS builder, and the CSS strings and DOM
 * applier for the brand colours (their mapping lives in `themeColors.ts`).
 * See aishop `web_client/layout.tsx` + `themeStyle.ts`.
 */
import { findGoogleFont, buildGoogleFontsHref } from "./googleFonts.js";
import { darkModeGradientVars, darkModeVars, gradientVars, themeBrandVars } from "./themeColors.js";

// The ink rule, the brand-colour mapping and the gradient live in `themeColors.ts`
// (no font catalogue behind it); re-exported here so existing imports keep working.
export { foregroundFor, gradientVars, isDarkColor } from "./themeColors.js";

export type GradientMode = "brand" | "fade" | "custom";

export interface FontSpec {
  family?: string; // "" / missing = inherit (see INHERITS_FROM); on primary/secondary the system font
  weight?: number; // 400 by default
  size?: string;   // CSS value (e.g. "1rem", "32px"); missing = inherited
}

export interface GradientSpec {
  enabled?: boolean;
  mode?: GradientMode;
  customFrom?: string;
  customTo?: string;
  angle?: number;
}

export type FontKey =
  | "primary" | "secondary"
  | "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  | "button" | "link";

export const FONT_KEYS: FontKey[] = [
  "primary", "secondary", "h1", "h2", "h3", "h4", "h5", "h6", "button", "link",
];

/** Login/registration page (host-level) layout and background. */
export type AuthLayout = "centered" | "split-left" | "split-right";
export type AuthBgEffect = "mesh" | "aurora" | "conic" | "gradient" | "none";
export type AuthGlow = "aurora" | "conic" | "pulse" | "spectrum";

export interface AuthLayoutSpec {
  /** Layout - centered or a 50/50 split (photo/effect on one side). Default "centered". */
  layout?: AuthLayout;
  /** Animated background effect (used when `bgImage` is missing). Default "mesh". */
  bgEffect?: AuthBgEffect;
  /** Photo from the media library - when set it replaces the effect. */
  bgImage?: string;
  /** Optional dark variant of the photo. */
  bgImageDark?: string;
  /** Effect/photo overlay intensity 0-100. Default 55. */
  intensity?: number;
  /** Submit button (CTA) glow style. Default "aurora". */
  glow?: AuthGlow;
}

/**
 * Subtle effects of the "Alusta" page (the result view of the public signup +
 * WP import funnel) - confetti on completion, card border glow and the "building"
 * loading background. Only meaningful on the host site; managed in the
 * Kohanda → Alusta section and served by `/public/host-theme` as the `funnel` block
 * (from the host `funnel.*` SiteSettings).
 */
export type ConfettiVariant =
  | "realistic"
  | "basic"
  | "fireworks"
  | "stars"
  | "snow"
  | "schoolPride";
/** Border glow style of the result card. */
export type CardBorderStyle = "beam" | "animated" | "static" | "none";
/** Loading background of the "Ehitame su saiti" state (behind the card). */
export type LoadingEffect = "orb" | "aurora" | "pulse" | "none";

export interface FunnelEffectsSpec {
  /** Confetti on completion. On by default. */
  confetti?: boolean;
  /** Confetti type. Default "realistic". */
  confettiVariant?: ConfettiVariant;
  /** Border style of the result card. Default "beam". */
  cardBorder?: CardBorderStyle;
  /** Loading background of the "Ehitame" state. Default "orb". */
  loadingEffect?: LoadingEffect;
}

export interface SiteTheme {
  primary?: string;
  secondary?: string;
  /**
   * The brand colours dark mode uses instead of `primary` and `secondary`. Empty =
   * the same colour in both modes (every site until 2026-10-17). One colour rarely
   * reads well as text on both a white and a near-black page: husly.app's copper
   * passes 4.5:1 on black but only 2.9:1 on white, a deeper copper the other way
   * round. A brand or fade gradient follows these in dark mode; a custom gradient
   * keeps its own two ends. See `darkModeTheme` in `themeColors.ts`.
   */
  darkPrimary?: string;
  darkSecondary?: string;
  accent?: string;
  /** Page background color in LIGHT mode (`--color-background`). Empty = @wa/ui
   *  default (light gray). WP import puts the original site's body background
   *  here (typically pure white) so an imported site does not look grayish. */
  background?: string;
  radius?: number;
  gradient?: GradientSpec;
  logoUrl?: string;
  logoUrlDark?: string;
  faviconUrl?: string;
  fonts?: Partial<Record<FontKey, FontSpec>>;
  /** Login page layout + background (only meaningful on the host site). */
  auth?: AuthLayoutSpec;
  /** Effects of the "Alusta" funnel result view - confetti/glow/loading (host only).
   *  Not stored in `themeJson`; `/public/host-theme` merges it in from the host
   *  `funnel.*` SiteSettings (managed in the Kohanda → Alusta section). */
  funnel?: FunnelEffectsSpec;
  /** Legacy simple model (the earlier Themes page) - read as a fallback. */
  fontHeading?: string;
  fontBody?: string;
}

/** Full CSS stack of a font family, with the matching generic fallback. */
export function fontStackFor(family: string | null | undefined): string | null {
  if (!family) return null;
  const meta = findGoogleFont(family);
  const generic = meta
    ? meta[1] === "monospace" ? "monospace"
    : meta[1] === "serif" ? "serif"
    : meta[1] === "display" || meta[1] === "handwriting" ? "cursive"
    : "sans-serif"
    : "sans-serif";
  return `"${family}", system-ui, ${generic}`;
}

/** Merge the detailed `fonts` + legacy fontBody/fontHeading into a single set. */
export function normalizeFonts(theme: SiteTheme | null | undefined): Partial<Record<FontKey, FontSpec>> {
  const fonts: Partial<Record<FontKey, FontSpec>> = { ...(theme?.fonts ?? {}) };
  if (!fonts.primary?.family && theme?.fontBody) fonts.primary = { ...fonts.primary, family: theme.fontBody };
  if (!fonts.secondary?.family && theme?.fontHeading) fonts.secondary = { ...fonts.secondary, family: theme.fontHeading };
  return fonts;
}

/**
 * Where a key with no family of its own ("" = inherit) gets its family on the
 * published site; the first base key with a family wins. Headings take
 * `--font-display`, the secondary family (the @wa/ui base rule for h1-h3, rich text
 * and the widget headings for every level); without a secondary family the primary
 * one is the only theme face they can land on. Buttons and links inherit the body
 * text, the primary family. The base keys inherit nothing: an empty primary or
 * secondary family is the system font.
 */
const INHERITS_FROM: Partial<Record<FontKey, readonly FontKey[]>> = {
  h1: ["secondary", "primary"],
  h2: ["secondary", "primary"],
  h3: ["secondary", "primary"],
  h4: ["secondary", "primary"],
  h5: ["secondary", "primary"],
  h6: ["secondary", "primary"],
  button: ["primary"],
  link: ["primary"],
};

/** The family a key with no family of its own is set in (see `INHERITS_FROM`). */
function inheritedFamily(fonts: Partial<Record<FontKey, FontSpec>>, key: FontKey): string | undefined {
  for (const base of INHERITS_FROM[key] ?? []) {
    const family = fonts[base]?.family;
    if (family) return family;
  }
  return undefined;
}

/**
 * A family outside the Google catalogue that the platform serves from its own files, with
 * the weights it has (`ownFamily` of `@wa/widgets/font-library`). Core cannot read that
 * list itself (the widgets package depends on core), so a caller that loads such families
 * hands the lookup in.
 */
export type OwnFontLookup = (family: string) => { family: string; weights: readonly number[] } | null | undefined;

/** Fonts used by the theme (family, weights) - the shared basis for the self-hosted
 *  `@font-face` CSS (R2 mirror) AND the Google fallback link. Google families only,
 *  unless `own` names the platform's own families too (the font library, the house
 *  faces); the Google link builders skip those, the mirror serves them. */
export function themeFontSpecs(theme: SiteTheme | null | undefined, own?: OwnFontLookup): Array<{ family: string; weights: number[] }> {
  const fonts = normalizeFonts(theme);
  const familyWeights = new Map<string, Set<number>>();
  const add = (family: string, weights: number[]) => {
    const set = familyWeights.get(family) ?? new Set<number>();
    for (const w of weights) set.add(w);
    familyWeights.set(family, set);
  };
  const known = (family: string | undefined): { family: string; weights: readonly number[] } | undefined => {
    const meta = findGoogleFont(family);
    if (meta) return { family: meta[0], weights: meta[2] };
    return (family && own?.(family)) || undefined;
  };
  for (const k of FONT_KEYS) {
    const spec = fonts[k];
    if (spec?.family) {
      const meta = known(spec.family);
      // Body text often needs 400 too, even when the heading is heavier.
      if (meta) add(meta.family, [spec.weight ?? 400, 400]);
      continue;
    }
    // An inheriting key still sets its weight (`buildThemeFontCss` writes it), so the
    // inherited family has to load it, or the browser borrows the nearest face (a
    // missing 600 renders with the 700). Only a weight the family has: Google's CSS2
    // API rejects the whole request over one it lacks.
    const weight = spec?.weight;
    if (!weight) continue;
    const meta = known(inheritedFamily(fonts, k));
    if (meta?.weights.includes(weight)) add(meta.family, [weight]);
  }
  return [...familyWeights].map(([family, ws]) => ({ family, weights: [...ws] }));
}

/** Minimal Google Fonts CSS2 URL for all the used (family, weight) pairs. */
export function themeFontsHref(theme: SiteTheme | null | undefined): string | null {
  return buildGoogleFontsHref(themeFontSpecs(theme));
}

/**
 * Prefix a selector list with a scope root (itself possibly a list). `:root` and
 * `body` collapse INTO the scope root - on a scoped surface the root element is
 * the surface itself, not the document. Without a scope the selectors pass
 * through unchanged (the published site keeps its document-level rules).
 */
function scopeSelector(selectors: string, scope: string | undefined): string {
  if (!scope) return selectors;
  const roots = scope.split(",").map((s) => s.trim()).filter(Boolean);
  const parts = selectors.split(",").map((s) => s.trim()).filter(Boolean);
  const out: string[] = [];
  for (const root of roots) {
    for (const sel of parts) out.push(sel === ":root" || sel === "body" ? root : `${root} ${sel}`);
  }
  return out.join(", ");
}

export interface ThemeFontCssOptions {
  /**
   * Selector the rules are scoped under (e.g. `[data-canvas-root]`). Set it for
   * editing surfaces: a tenant may pick a display face that is unreadable in the
   * admin chrome, so the font must never escape the surface it belongs to.
   */
  scope?: string;
  /**
   * The surface sits on a page that has theme fonts of its OWN (a design preview in
   * the designs gallery of the public site). That page's rules are bare element rules
   * (`h4 { ... }`, `a { ... }`), so they reach into the surface wherever this theme
   * sets nothing. With `isolate` the CSS first rolls them back to the layered base
   * (`revert-layer`: @wa/ui + Tailwind preflight), which is what a published site with
   * this theme has there, and the surface root takes the place of `body`.
   *
   * Wrap the `scope` in `:where()` for this. Every rule then keeps the specificity the
   * published site gives it: the host's rule loses on document order (it comes
   * first), and a widget's own class rule still wins, the same as on the published
   * site. Needs `scope`. A theme with no fonts still produces no CSS.
   */
  isolate?: boolean;
}

/** The selectors of the theme's button rule (one font for all three). */
const BUTTON_SELECTORS = 'button, .btn, [role="button"]';

/**
 * `isolate`: undo the host page's element font rules inside the scope. Every selector
 * is prefixed on its own (no `:is()` grouping), so each keeps its own specificity -
 * grouped, `a` would take the class weight of `.btn` and beat a widget's own class rule.
 */
function isolationRules(scope: string): string[] {
  return [
    `${scopeSelector("body", scope)} { font-family: var(--font-sans); font-weight: 400; }`,
    `${scopeSelector(`h1, h2, h3, h4, h5, h6, ${BUTTON_SELECTORS}, a`, scope)} { font-family: revert-layer; font-weight: revert-layer; font-size: revert-layer; }`,
  ];
}

function ruleFor(spec: FontSpec | undefined): string | null {
  if (!spec) return null;
  const parts: string[] = [];
  const stack = fontStackFor(spec.family);
  if (stack) parts.push(`font-family: ${stack}`);
  if (spec.weight) parts.push(`font-weight: ${spec.weight}`);
  if (spec.size) parts.push(`font-size: ${spec.size}`);
  return parts.length ? parts.join("; ") + ";" : null;
}

/**
 * Element-targeted font CSS (body, h1..h6, button, a) + the `--font-sans`/
 * `--font-display`/`--font-secondary` variables. Empty string if nothing to set.
 *
 * `opts.scope` confines every rule to one surface (the builder canvas, the widget
 * previews) - see `scopeSelector`. `opts.isolate` also shuts out the host page's own
 * font rules (a design preview on a themed page) - see `ThemeFontCssOptions`.
 */
export function buildThemeFontCss(
  theme: SiteTheme | null | undefined,
  opts?: ThemeFontCssOptions,
): string {
  const scope = opts?.scope;
  const fonts = normalizeFonts(theme);
  const rules: string[] = [];
  const rootVars: string[] = [];

  const primaryStack = fontStackFor(fonts.primary?.family);
  if (primaryStack) rootVars.push(`--font-sans: ${primaryStack}`);
  const secStack = fontStackFor(fonts.secondary?.family);
  if (secStack) {
    rootVars.push(`--font-secondary: ${secStack}`);
    rootVars.push(`--font-display: ${secStack}`);
  }

  const body = ruleFor(fonts.primary);
  if (body) rules.push(`${scopeSelector("body", scope)} { ${body} }`);
  for (const tag of ["h1", "h2", "h3", "h4", "h5", "h6"] as const) {
    const r = ruleFor(fonts[tag]);
    if (r) rules.push(`${scopeSelector(tag, scope)} { ${r} }`);
  }
  const btn = ruleFor(fonts.button);
  if (btn) rules.push(`${scopeSelector(BUTTON_SELECTORS, scope)} { ${btn} }`);
  const link = ruleFor(fonts.link);
  if (link) rules.push(`${scopeSelector("a", scope)} { ${link} }`);

  if (rootVars.length) rules.unshift(`${scopeSelector(":root", scope)} { ${rootVars.join("; ")}; }`);
  // The reset goes FIRST: it shares the specificity of the theme's own rules, so
  // those win on order wherever the theme sets something.
  if (opts?.isolate && scope && rules.length) rules.unshift(...isolationRules(scope));
  return rules.join("\n");
}

/**
 * The theme's BRAND COLOURS + corner radius as a server-renderable CSS string.
 *
 * For the places that can only emit text: the public site's page, the template
 * preview and the static site export. The variables are `themeBrandVars` (see
 * `themeColors.ts`), the same mapping the builder canvas (`themeSurfaceVars`) and
 * the admin/account shell (`applyThemeColorVars`) start from, so a brand colour
 * gets the same ink and the same secondary fill everywhere.
 *
 * `:root:not(.dark)` for the background is deliberate: specificity (0,2,0) beats
 * @wa/ui's `:root` default without overriding the dark theme's own background.
 * A theme with dark-mode brand colours (`darkPrimary`, `darkSecondary`) gets a
 * `.dark{...}` rule after the `:root` one, holding only the variables that differ;
 * same specificity, later in the sheet, so it wins on the page in dark mode and in
 * any `.dark` part of it. Fonts are NOT here - see `buildThemeFontCss` / `themeFontsHref`.
 */
export function themeColorCss(theme: SiteTheme | null | undefined): string {
  if (!theme) return "";
  const vars = Object.entries(themeBrandVars(theme, "content"));
  const root = vars.length ? `:root{${vars.map(([k, v]) => `${k}:${v}`).join(";")}}` : "";
  const bg = theme.background ? `:root:not(.dark){--color-background:${theme.background}}` : "";
  const darkVars = Object.entries(darkModeVars(theme, "content"));
  const dark = darkVars.length ? `.dark{${darkVars.map(([k, v]) => `${k}:${v}`).join(";")}}` : "";
  return root + bg + dark;
}

/**
 * A single CSS string of the gradient variables (`:root { ... }`), for web_site. A
 * gradient that follows dark-mode brand colours gets a `.dark { ... }` rule too (see
 * `themeColorCss`); it has to live here, after this `:root`, or this one would win.
 */
export function gradientCss(theme: SiteTheme | null | undefined): string {
  const vars = gradientVars(theme);
  const entries = Object.entries(vars);
  if (!entries.length) return "";
  const darkVars = Object.entries(darkModeGradientVars(theme));
  const dark = darkVars.length ? ` .dark { ${darkVars.map(([k, v]) => `${k}: ${v}`).join("; ")}; }` : "";
  return `:root { ${entries.map(([k, v]) => `${k}: ${v}`).join("; ")}; }${dark}`;
}

/**
 * Every custom property `applyThemeColorVars` owns on its root: set when the theme
 * defines it, removed when it does not (so switching sites never leaves the last
 * site's colour behind).
 */
export const SHELL_THEME_VARS = [
  "--color-primary",
  "--color-primary-foreground",
  "--color-brand-gradient-foreground",
  "--color-primary-pair-foreground",
  "--color-primary-2",
  "--color-primary-dark",
  "--color-ring",
  "--color-accent-brand",
  "--color-accent-brand-foreground",
  "--color-gradient-from",
  "--color-gradient-to",
  "--gradient-angle",
  "--radius-base",
] as const;

/**
 * Apply the theme BRAND COLORS + radius + gradient straight onto an app shell's
 * root element (CSS variables). The one applier for the admin shell (web_admin
 * `adminTheme.ts`) and self service (web_account), so login, cards and aurora follow
 * the host theme identically. Uses the `chrome` mapping of `themeBrandVars`: the
 * kit's neutral `--color-secondary` stays, see `ThemeVarSurface`. Fonts are NOT
 * applied here (see `buildThemeFontCss` / admin `adminTheme.ts`). `null` = reset.
 */
export function applyThemeColorVars(
  root: HTMLElement,
  theme: SiteTheme | null | undefined,
): void {
  const vars: Record<string, string> = { ...themeBrandVars(theme, "chrome"), ...gradientVars(theme) };
  for (const name of SHELL_THEME_VARS) {
    const value = vars[name];
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name);
  }
}
